'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import { AdornedInputContext } from './context/adorned-input.context';
import { adornedInputInputStyles } from './styles/adorned-input-input.styles';
import type { AdornedInputInputPropsInterface } from './types/adorned-input-input.types';

/**
 * @since 10.0.0
 */
export const AdornedInputInput = forwardRef<HTMLInputElement, AdornedInputInputPropsInterface>(
  (props, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const { adornmentIds = [], size } = useContext(AdornedInputContext);

    const {
      className,
      css: cssProp,
      disabled = formFieldContext.disabled || false,
      error = formFieldContext.error || false,
      readOnly = formFieldContext.readOnly || false,
      required = formFieldContext.required || false,
      type = 'text',
      ...rest
    } = props;

    const describedBy = useMemo(() => {
      const ids = [...adornmentIds, formFieldContext.descriptionId].filter(Boolean);
      return ids.length ? ids.join(' ') : undefined;
    }, [adornmentIds, formFieldContext.descriptionId]);

    return (
      <input
        ref={forwardedRef}
        className={cx('c11n', css(adornedInputInputStyles.raw({ size }), cssProp), className)}
        data-c11n-component="AdornedInput.Input"
        data-c11n-version={__C11N_VERSION__}
        id={formFieldContext.controlId}
        type={type}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    );
  },
);

AdornedInputInput.displayName = 'AdornedInput.Input';
